import React, { useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Alert,
  LinearProgress,
  Chip,
  Paper
} from '@mui/material';
import apiClient from '../services/api';

// 都道府県データ
const prefectures = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
  '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
  '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
  '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
];

// トピックタイプ
const topicTypes = [
  { value: '地域イベント・祭り', emoji: '🎭', description: '地域の祭りやイベント情報' },
  { value: '公共サービス', emoji: '🏛️', description: '行政サービスや手続き案内' },
  { value: '観光・レジャー', emoji: '🗻', description: '観光地や地域の魅力' },
  { value: '交通・インフラ', emoji: '🚊', description: '交通情報やインフラ整備' },
  { value: '教育・文化', emoji: '📚', description: '教育機関や文化施設情報' },
  { value: '医療・福祉', emoji: '🏥', description: '医療機関や福祉サービス' },
  { value: '産業・経済', emoji: '🏭', description: '地域産業や経済情報' },
  { value: '環境・防災', emoji: '🌱', description: '環境保護や防災対策' }
];

const JobStatus = {
  PENDING: 'pending',
  GENERATING: 'generating',
  CREATING_NOTION_PAGE: 'creating_notion_page',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

const AdminLocalNewsGenerator: React.FC = () => {
  const [selectedPrefecture, setSelectedPrefecture] = useState<string>('');
  const [selectedTopicType, setSelectedTopicType] = useState<string>('');
  const [keywords, setKeywords] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string>('');
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string>('');

  // ジョブステータスをポーリングする関数
  const pollJobStatus = async (jobId: string) => {
    const maxAttempts = 60; // 最大5分間ポーリング
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await apiClient.get(`/admin/job-status/${jobId}`);
        const data = response.data;
        
        setJobStatus(data.status);
        
        if (data.status === JobStatus.COMPLETED) {
          setResult(data.result);
          setIsGenerating(false);
          return;
        } else if (data.status === JobStatus.FAILED) {
          setError(data.result?.error || '記事生成に失敗しました');
          setIsGenerating(false);
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000); // 5秒後に再試行
        } else {
          setError('タイムアウト: 記事生成に時間がかかりすぎています');
          setIsGenerating(false);
        }
      } catch (error) {
        console.error('ステータス確認エラー:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setError('ステータス確認に失敗しました');
          setIsGenerating(false);
        }
      }
    };

    poll();
  };

  const handleGenerate = async () => {
    if (!selectedPrefecture || !selectedTopicType) {
      setError('都道府県とトピックタイプを選択してください');
      return;
    }

    setIsGenerating(true);
    setError('');
    setResult(null);
    setJobId(null);
    setJobStatus('');

    try {
      const response = await apiClient.post('/admin/generate-article-quick', {
        prefecture: selectedPrefecture,
        topicType: selectedTopicType,
        keywords: keywords
      });

      const data = response.data;

      if (data.success) {
        setJobId(data.job_id);
        setJobStatus(JobStatus.PENDING);
        // ポーリング開始
        pollJobStatus(data.job_id);
      } else {
        setError(data.error || '記事生成に失敗しました');
        setIsGenerating(false);
      }
    } catch (error) {
      console.error('記事生成エラー:', error);
      setError('記事生成に失敗しました');
      setIsGenerating(false);
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case JobStatus.PENDING:
        return '記事生成を開始しています...';
      case JobStatus.GENERATING:
        return '記事を生成中です（1500-2000文字の質の高い記事を作成しています）...';
      case JobStatus.CREATING_NOTION_PAGE:
        return 'Notionページを作成中です...';
      case JobStatus.COMPLETED:
        return '記事生成が完了しました！';
      case JobStatus.FAILED:
        return '記事生成に失敗しました';
      default:
        return '処理中...';
    }
  };

  const getProgress = (status: string) => {
    switch (status) {
      case JobStatus.PENDING:
        return 10;
      case JobStatus.GENERATING:
        return 60;
      case JobStatus.CREATING_NOTION_PAGE:
        return 90;
      case JobStatus.COMPLETED:
        return 100;
      default:
        return 0;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
        }}
      >
            <CardContent>
              <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold', color: '#667eea' }}>
                🤖 AI地方自治記事ジェネレーター
              </Typography>
              <Typography variant="h6" component="h2" gutterBottom align="center" sx={{ color: '#666' }}>
                1500-2000文字の質の高い地方自治情報記事を自動生成
              </Typography>

              {/* 都道府県選択 */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  📍 都道府県を選択
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {prefectures.map((prefecture) => (
                    <Box key={prefecture} sx={{ flexBasis: { xs: 'calc(50% - 4px)', sm: 'calc(25% - 6px)', md: 'calc(20% - 6.4px)', lg: 'calc(16.66% - 6.8px)' } }}>
                      <Card
                        onClick={() => setSelectedPrefecture(prefecture)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          background: selectedPrefecture === prefecture 
                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            : 'rgba(255, 255, 255, 0.9)',
                          color: selectedPrefecture === prefecture ? 'white' : 'text.primary',
                          border: selectedPrefecture === prefecture ? '2px solid #667eea' : '1px solid rgba(255, 255, 255, 0.3)',
                          transform: selectedPrefecture === prefecture ? 'scale(1.05)' : 'scale(1)',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.5)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="body2" align="center">
                            {prefecture}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* トピックタイプ選択 */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  📝 記事のトピックを選択
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={2}>
                  {topicTypes.map((topic) => (
                    <Box key={topic.value} sx={{ flexBasis: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.33% - 10.66px)' } }}>
                      <Card
                        onClick={() => setSelectedTopicType(topic.value)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          background: selectedTopicType === topic.value 
                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            : 'rgba(255, 255, 255, 0.9)',
                          color: selectedTopicType === topic.value ? 'white' : 'text.primary',
                          border: selectedTopicType === topic.value ? '2px solid #667eea' : '1px solid rgba(255, 255, 255, 0.3)',
                          transform: selectedTopicType === topic.value ? 'scale(1.05)' : 'scale(1)',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.5)',
                          },
                        }}
                      >
                        <CardContent>
                          <Box display="flex" alignItems="center" mb={1}>
                            <Typography variant="h5" sx={{ mr: 1 }}>
                              {topic.emoji}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {topic.value}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            {topic.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* キーワード入力 */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  🔍 キーワード（オプション）
                </Typography>
                <Box
                  component="input"
                  type="text"
                  value={keywords}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeywords(e.target.value)}
                  placeholder="記事に含めたいキーワードを入力（例：新しい施設、イベント名など）"
                  sx={{
                    width: '100%',
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid #ddd',
                    fontSize: '16px',
                    '&:focus': {
                      outline: 'none',
                      borderColor: '#667eea',
                    }
                  }}
                />
              </Box>

              {/* 記事生成ボタン */}
              <Box mb={4} textAlign="center">
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGenerate}
                  disabled={isGenerating || !selectedPrefecture || !selectedTopicType}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    py: 2,
                    px: 4,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    }
                  }}
                >
                  {isGenerating ? '🔄 記事生成中...' : '✨ 記事を生成する'}
                </Button>
              </Box>

              {/* ジョブ進行状況 */}
              {isGenerating && jobStatus && (
                <Box mb={4}>
                  <Paper sx={{ p: 3, background: 'rgba(102, 126, 234, 0.1)' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      📊 生成進行状況
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {getStatusMessage(jobStatus)}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={getProgress(jobStatus)} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        mt: 2,
                        background: 'rgba(255,255,255,0.3)',
                        '& .MuiLinearProgress-bar': {
                          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                        }
                      }} 
                    />
                    <Typography variant="body2" sx={{ mt: 1, textAlign: 'right' }}>
                      {getProgress(jobStatus)}%
                    </Typography>
                    {jobId && (
                      <Typography variant="body2" sx={{ mt: 2, opacity: 0.7 }}>
                        ジョブID: {jobId}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}

              {/* エラー表示 */}
              {error && (
                <Alert severity="error" sx={{ mb: 4 }}>
                  {error}
                </Alert>
              )}

              {/* 結果表示 */}
              {result && (
                <Box>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    ✅ 記事が正常に生成され、Notionに保存されました！
                  </Alert>
                  
                  <Card
                    sx={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 2,
                      boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                      border: '1px solid rgba(255, 255, 255, 0.18)',
                      my: 2,
                    }}
                  >
                    <CardContent>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#667eea' }}>
                        📰 生成された記事
                      </Typography>
                      
                      <Box mb={3}>
                        <Chip label={`📍 ${result.prefecture}`} sx={{ mr: 1, mb: 1 }} />
                        <Chip label={`📝 ${result.topic_type}`} sx={{ mr: 1, mb: 1 }} />
                        <Chip label={`📏 ${result.article_length}文字`} sx={{ mr: 1, mb: 1 }} color="primary" />
                      </Box>

                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {result.title}
                      </Typography>
                      
                      <Paper sx={{ p: 3, background: '#f9f9f9', maxHeight: 400, overflow: 'auto' }}>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                          {result.article}
                        </Typography>
                      </Paper>

                      {result.citations && result.citations.length > 0 && (
                        <Box mt={3}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            📚 参考情報
                          </Typography>
                          <Paper sx={{ p: 2, background: '#f0f0f0' }}>
                            {result.citations.map((citation: string, index: number) => (
                              <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                                • {citation}
                              </Typography>
                            ))}
                          </Paper>
                        </Box>
                      )}

                      <Box mt={3}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          Notion ページID: {result.notion_page_id}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}
          </CardContent>
        </Card>
    </Box>
  );
};

export default AdminLocalNewsGenerator;
